"use client";

import React, { useEffect, useState, use } from "react";
import homepageApi from "@/services/homepageApi";
import HomepageViewer from "@/components/shared/homepage/HomepageViewer";
import LandingPage from "@/components/shared/landing-page/LandingPage";
import { SectionData } from "@/components/shared/homepage/types";
import { PiSpinner } from "react-icons/pi";

interface PageProps {
  params: Promise<{
    subdomain: string;
  }>;
}

export default function TenantSite({ params }: PageProps) {
  const { subdomain } = use(params);
  const [sections, setSections] = useState<SectionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [logo, setLogo] = useState<string | undefined>(undefined);
  const [useDefaultLanding, setUseDefaultLanding] = useState(false);

  const loadSite = async () => {
    try {
      if (typeof window !== "undefined" && subdomain) {
        const cachedHomepage = localStorage.getItem(`homepage_${subdomain}`);
        if (cachedHomepage) {
          try {
            const parsed = JSON.parse(cachedHomepage);
            if (parsed.success && parsed.data && parsed.data.length > 0) {
              setSections(parsed.data);
              const detectedLogo = parsed.subdomainInfo?.logo || parsed.ownerDetails?.logo;
              if (detectedLogo) setLogo(detectedLogo);
              setUseDefaultLanding(false);
              setLoading(false);
            }
          } catch (e) {
            console.error("Failed to parse cached homepage", e);
          }
        }
      }

      // If we don't have a cached version, set loading to true
      if (sections.length === 0) {
        setLoading(true);
      }

      const response = await homepageApi.loadBySubdomain(subdomain);
      console.log("[TenantSite] API response for subdomain:", subdomain);
      console.log("[TenantSite] response.success:", response.success);
      console.log("[TenantSite] response.data:", response.data);
      console.log("[TenantSite] response.data length:", response.data?.length);
      console.log("[TenantSite] response.subdomainInfo:", response.subdomainInfo);
      console.log("[TenantSite] response.data is array:", Array.isArray(response.data));

      if (response.success && response.data && response.data.length > 0) {
        if (typeof window !== "undefined") {
          localStorage.setItem(`homepage_${subdomain}`, JSON.stringify(response));
          const ownerId = response.ownerDetails?.id || response.subdomainInfo?.userId || "";
          const ownerUuid = response.subdomainInfo?.uuid || response.ownerDetails?.uuid || "";
          if (ownerId) {
            localStorage.setItem("subdomain_institution_id", String(ownerId));
          }
          if (ownerUuid) {
            localStorage.setItem("subdomain_institution_uuid", ownerUuid);
          }
        }
        setSections(response.data);

        const detectedLogo = response.subdomainInfo?.logo || response.ownerDetails?.logo;
        if (detectedLogo) {
          setLogo(detectedLogo);
          if (typeof window !== "undefined") {
            import("@/utils/image-url").then(({ getImageUrl }) => {
              const fullLogoUrl = detectedLogo.startsWith("http") ? detectedLogo : (getImageUrl(detectedLogo) || detectedLogo);
              localStorage.setItem(`logo_${subdomain}`, fullLogoUrl);
            });
          }
        }
        setUseDefaultLanding(false);
      } else {
        console.log("[TenantSite] No homepage data found, using default landing page");
        console.log("[TenantSite] response:", JSON.stringify(response, null, 2));
        setUseDefaultLanding(true);
      }
    } catch (err) {
      console.error("Failed to load tenant site:", err);
      if (sections.length === 0) setUseDefaultLanding(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (subdomain) {
      document.title = `${subdomain}-Home Page`;
      loadSite();
    }
  }, [subdomain]);

  if (loading) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center bg-white">
        <PiSpinner className="w-8 h-8 animate-spin text-blue-600 mb-4" />
        <p className="text-gray-500 font-medium">Loading {subdomain}...</p>
      </div>
    );
  }

  if (useDefaultLanding) {
    return <LandingPage subdomain={subdomain} />;
  }

  return (
    <HomepageViewer
      sections={sections}
      defaultLogo={logo}
    />
  );
}